import type { AuthConfig, Me } from "@chai/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, createContext, useCallback, useContext } from "react";

import { useApi } from "./api";

export interface AuthState {
  authConfig: AuthConfig | undefined;
  me: Me | null | undefined;
  isLoading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const queryClient = useQueryClient();

  const authConfigQuery = useQuery({
    queryKey: ["auth-config"],
    queryFn: () => api.authConfig(),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api.me();
      } catch (err) {
        if ((err as { status?: number }).status === 401) return null;
        throw err;
      }
    },
    retry: false,
  });

  const refresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["me"] }),
      queryClient.invalidateQueries({ queryKey: ["auth-config"] }),
    ]);
  }, [queryClient]);

  const logout = useCallback(async () => {
    await api.logout().catch(() => undefined);
    queryClient.setQueryData(["me"], null);
    queryClient.clear();
  }, [api, queryClient]);

  const value: AuthState = {
    authConfig: authConfigQuery.data,
    me: meQuery.data,
    isLoading: authConfigQuery.isLoading || meQuery.isLoading,
    refresh,
    logout,
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth() must be used inside <AuthProvider>");
  return ctx;
}
